import type { Slot } from "@medplum/fhirtypes";
import { getTRPCClient, memberKey } from "@repo/api";
import {
  type BlockAvailabilityInput,
  blockAvailabilityInputSchema,
  scheduleBlockIdSchema,
  scheduleIdSchema,
  type UnblockAvailabilityInput,
  unblockAvailabilityInputSchema,
} from "@repo/api/schemas";
import type { ScheduleBlockId, ScheduleId } from "@repo/domain";
import { extensionUrl, identifierSystem } from "@repo/fhir-systems";
import { BLOCK_HIGH_WATER_URL } from "@repo/fhir-systems/extensions";
import { SCHEDULE_BLOCK_ID_SYSTEM, SCHEDULE_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import type { Collection } from "@tanstack/db";
import { useMemo } from "react";
import { defineWrite } from "../actions/define-write";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { ScheduleRow, SlotRow } from "../fhir/types";
import { useSlots } from "./slot";

const SLOT_COLLECTION = "slot";
const RECURRING_PATTERN_URL = extensionUrl("RecurringPattern");

type DayOfWeek = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

interface RecurringPattern {
  frequency: "daily" | "weekly";
  dayOfWeek?: DayOfWeek[];
  startDate?: string;
  endDate?: string;
}

/**
 * AvailabilityBlock — provider-time blocked off a Schedule.
 *
 * Read model over FHIR `Slot` rows with `status="busy-unavailable"`. Discriminated
 * by `blockType`: `one-time` blocks carry ISO datetime ranges; `recurring` blocks
 * carry HH:mm clock ranges plus a recurrence pattern.
 */
export interface AvailabilityBlockDto {
  blockId: ScheduleBlockId;
  scheduleId: ScheduleId;
  blockType: "one-time" | "recurring";
  /** ISO datetime for one-time blocks, HH:mm for recurring blocks */
  start: string;
  /** ISO datetime for one-time blocks, HH:mm for recurring blocks */
  end: string;
  reason?: string;
  recurring?: RecurringPattern;
}

function toClock(value: string | undefined): string {
  if (!value) return "";
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  return value.slice(11, 16);
}

function extensionToRecurringPattern(extensions?: Slot["extension"]): RecurringPattern | undefined {
  const patternExt = extensions?.find((e) => e.url === RECURRING_PATTERN_URL);
  if (!patternExt?.extension) return undefined;

  const findExt = (url: string) => patternExt.extension?.find((e) => e.url === url);
  const findAllExt = (url: string) => patternExt.extension?.filter((e) => e.url === url) ?? [];

  const frequency = findExt("frequency")?.valueCode;
  if (frequency !== "daily" && frequency !== "weekly") return undefined;

  const days = findAllExt("dayOfWeek")
    .map((e) => e.valueCode)
    .filter((v): v is DayOfWeek => !!v);
  const startDate = findExt("startDate")?.valueDate;
  const endDate = findExt("endDate")?.valueDate;

  return {
    frequency,
    ...(days.length ? { dayOfWeek: days } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
}

function toDto(slot: Slot): AvailabilityBlockDto {
  const blockIdentity = slot.identifier?.find(
    (identifier) => identifier.system === identifierSystem("schedule-block"),
  )?.value;
  const [scheduleId, blockId] = blockIdentity?.split("|") ?? [];
  if (!scheduleId || !blockId) throw new Error("Schedule block is missing its parent-scoped identity");
  const recurring = extensionToRecurringPattern(slot.extension);

  if (recurring) {
    return {
      blockId: scheduleBlockIdSchema.parse(blockId),
      scheduleId: scheduleIdSchema.parse(scheduleId),
      blockType: "recurring",
      start: toClock(slot.start),
      end: toClock(slot.end),
      ...(slot.comment ? { reason: slot.comment } : {}),
      recurring,
    };
  }

  return {
    blockId: scheduleBlockIdSchema.parse(blockId),
    scheduleId: scheduleIdSchema.parse(scheduleId),
    blockType: "one-time",
    start: slot.start ?? "",
    end: slot.end ?? "",
    ...(slot.comment ? { reason: slot.comment } : {}),
  };
}

interface UseAvailabilityBlocksResult {
  blocks: AvailabilityBlockDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/**
 * Live availability blocks for a single Schedule. Projects the generic
 * {@link useSlots} primitive filtered to `status="busy-unavailable"`.
 */
export function useAvailabilityBlocks(scheduleId: string | undefined): UseAvailabilityBlocksResult {
  const { slots, isLoading, isReady, isError } = useSlots(scheduleId, { status: "busy-unavailable" });
  const blocks = useMemo(() => slots.map(toDto), [slots]);
  return { blocks, isLoading, isReady, isError };
}

const slots = () => getDataStoreClient().getCollection(SLOT_COLLECTION);

export function optimisticAvailabilityBlock(input: BlockAvailabilityInput, blockId: string): SlotRow {
  const time = (value: string) =>
    input.blockType === "recurring" && /^\d{2}:\d{2}$/.test(value) ? `2000-01-01T${value}:00.000Z` : value;
  const recurring = input.recurring;
  return {
    resourceType: "Slot",
    status: "busy-unavailable",
    identifier: [{ system: SCHEDULE_BLOCK_ID_SYSTEM, value: `${input.scheduleId}|${blockId}` }],
    schedule: { identifier: { system: SCHEDULE_ENTITY_ID_SYSTEM, value: input.scheduleId } },
    slotKey: memberKey(input.scheduleId, blockId),
    scheduleJoinKey: input.scheduleId,
    start: time(input.start),
    end: time(input.end),
    comment: input.reason,
    extension: recurring
      ? [
          {
            url: RECURRING_PATTERN_URL,
            extension: [
              { url: "frequency", valueCode: recurring.frequency },
              ...(recurring.dayOfWeek ?? []).map((day) => ({ url: "dayOfWeek", valueCode: day })),
              ...(recurring.startDate ? [{ url: "startDate", valueDate: recurring.startDate }] : []),
              ...(recurring.endDate ? [{ url: "endDate", valueDate: recurring.endDate }] : []),
            ],
          },
        ]
      : undefined,
  };
}

export function createBlockAvailabilityAction(deps: {
  persist: Parameters<typeof defineWrite<BlockAvailabilityInput>>[0]["persist"];
  schedules: () => Collection<ScheduleRow, string>;
  slots: () => Collection<SlotRow, string>;
}) {
  const write = defineWrite<BlockAvailabilityInput>({
    optimistic: (params) => {
      const input = blockAvailabilityInputSchema.parse(params);
      const schedule = deps.schedules().get(input.scheduleId);
      const high = schedule?.extension?.find((ext) => ext.url === BLOCK_HIGH_WATER_URL)?.valueInteger;
      if (high === undefined) throw new DomainResultError("This schedule is not loaded. Refresh and try again.");
      deps.schedules().update(input.scheduleId, (draft) => {
        draft.extension = [
          ...(draft.extension ?? []).filter((ext) => ext.url !== BLOCK_HIGH_WATER_URL),
          { url: BLOCK_HIGH_WATER_URL, valueInteger: high + 1 },
        ];
      });
      deps.slots().insert(optimisticAvailabilityBlock(input, `block-${high + 1}`));
    },
    persist: deps.persist,
  });
  return async (input: BlockAvailabilityInput): Promise<void> => {
    await write(input).isPersisted.promise;
  };
}

export const blockAvailability = createBlockAvailabilityAction({
  persist: async (input, options) => {
    await getTRPCClient().schedule.blockAvailability.mutate(input, options);
  },
  schedules: () => getDataStoreClient().getCollection("schedule"),
  slots,
});

/** Intent: "unblock a previously blocked availability slot" */
export const unblockAvailability = defineWrite<UnblockAvailabilityInput>({
  optimistic: (params) => {
    const input = unblockAvailabilityInputSchema.parse(params);
    // Block ids are per-schedule sequences, so the full parent-scoped value is
    // the only unambiguous key — `block-1` alone exists on every calendar.
    const key = memberKey(input.scheduleId, input.blockId);
    if (!slots().has(key)) throw new Error(`Schedule block '${input.blockId}' is not loaded`);
    slots().delete(key);
  },
  persist: async (params, options) => {
    await getTRPCClient().schedule.unblockAvailability.mutate(params, options);
  },
});
